import { Button } from '@wot/shared';
import { ITutor } from '@wot/typings';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import categoryApi from '../../api/categories';
import { PPlanReqBody, plansApi } from '../../api/plan';
import tutorsApi from '../../api/tutors';
import { UNDER_REVIEW } from '../../assets/images';
import BottomSheet from '../../components/Bottomsheet';
import { useAppSelector } from '../../hooks';
import useCurrency from '../../hooks/useCurrency';
import { LPlan } from '../../interfaces/Plan';

function ExtendPlan({ plan, setShowExtendPlan }: { plan: LPlan; setShowExtendPlan: any }) {
  const navigate = useNavigate();
  const convertAmount = useCurrency();
  const { userData } = useAppSelector((state) => state.userSlice);
  const [noOfSessions, setNoOfSessions] = useState(0);
  const [range, setRange] = useState(1);
  const [costPerSession, setCostPerSession] = useState(200);
  const [paymentPending, setPaymentPending] = useState(false);
  const [discount] = useState(5);
  const today = new Date();
  const [tutor, setTutor] = useState<ITutor>();

  useEffect(() => {
    setRange(plan.purchasedSessions / 12);
    tutorsApi.getTutorById(plan.tutor._id).then((data) => setTutor(data));
  }, []);

  useEffect(() => {
    setNoOfSessions(range * 12);
  }, [range]);

  useEffect(() => {
    if (tutor) {
      if (!tutor.agreement?.level) {
        return;
      }
      categoryApi
        .getPricing(plan.categoryType, {
          tutorType: tutor.agreement?.level,
          grade: plan.categoryType === 'subject' ? plan.category.grade! : '',
          level: plan.categoryType === 'hobby' || plan.categoryType === 'skill' ? plan.category.level! : ''
        })
        .then((data) => {
          setCostPerSession(data);
        });
    }
  }, [tutor]);

  const payWithCash = () => {
    if (userData) {
      const newPlan: PPlanReqBody = {
        basePlanId: plan._id,
        userId: userData._id,
        tutorId: plan.tutor._id,
        categoryType: plan.categoryType,
        category: plan.category as any,
        purchasedSessions: noOfSessions,
        totalSessions: noOfSessions,
        noOfCancelsByStudent: { total: 3, occured: 0 },
        noOfCancelsByTutor: { total: 3, occured: 0 },
        noOfReschedulesByStudent: { total: noOfSessions, occured: 0 },
        noOfReschedulesByTutor: { total: noOfSessions, occured: 0 },
        costPerSession: Number(costPerSession),
        status: 'pending',
        discount,
        totalCost: (costPerSession * noOfSessions * (100 - discount)) / 100,
        paymentDetails: {
          modeOfPayment: 'cash' as const,
          status: 'pending'
        }
      };
      plansApi
        .add(userData._id, newPlan)
        .then((res) => {
          if (res && res.status === 'OK') {
            setPaymentPending(true);
          }
        })
        .catch((err) => err);
    }
  };

  const payOnline = () => {
    if (userData) {
      const newPlan: PPlanReqBody = {
        basePlanId: plan._id,
        userId: userData._id,
        tutorId: plan.tutor._id,
        categoryType: plan.categoryType,
        category: plan.category as any,
        purchasedSessions: noOfSessions,
        totalSessions: noOfSessions,
        noOfCancelsByStudent: { total: 3, occured: 0 },
        noOfCancelsByTutor: { total: 3, occured: 0 },
        noOfReschedulesByStudent: { total: noOfSessions, occured: 0 },
        noOfReschedulesByTutor: { total: noOfSessions, occured: 0 },
        costPerSession: Number(costPerSession),
        status: 'pending',
        discount,
        totalCost: (costPerSession * noOfSessions * (100 - discount)) / 100,
        paymentDetails: {
          modeOfPayment: 'online' as const,
          status: 'pending'
        }
      };
      plansApi
        .addPlanDraft(userData._id, newPlan)
        .then((data) => {
          if (data) {
            navigate('/buy-plan/checkout', {
              state: {
                plan: data
              }
            });
          }
        })
        .catch((err) => err);
    }
  };

  return (
    <BottomSheet>
      <div className={`w-full h-full flex flex-col overflow-y-scroll bg-gray-50`}>
        <div className={`sticky top-0 w-full flex items-center px-7 md:px-10 py-7`}>
          <h1 className="w-full mx-auto text-base md:text-xl text-center font-semibold">Extend Plan</h1>
          <MdClose className="right-0 text-2xl cursor-pointer ml-auto" onClick={() => setShowExtendPlan(false)} />
        </div>
        <div className="w-full h-full pb-4 px-10 max-h-[900px] flex flex-wrap items-stretch">
          {!paymentPending ? (
            <div className="w-full flex flex-col justify-center md:grid grid-flow-col grid-rows-3 grid-cols-5 gap-6">
              <div className="border bg-white col-span-3 rounded-md p-6">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-lg">Your Current Plan</h3>
                  <p className="text-sm px-2 py-1 bg-neutralBlue2">{`Plan ID: ${plan._id}`}</p>
                </div>
                <div className="flex flex-wrap w-full h-full items-center justify-between">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <p className="text-3xl font-bold text-primary">{plan.totalSessions}</p>
                      <p className="font-bold">Sessions</p>
                    </div>
                    <p className="text-sm">
                      ({plan.sessions?.length || 0}/{plan.totalSessions} remaining)
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="font-medium text-neutralGray2 text-sm">TUTOR</p>
                    <h3 className="font-bold">{plan.tutor.name.split(' ')[0]}</h3>
                  </div>
                  <div className="flex flex-col items-start justify-end gap-2">
                    <p className="font-medium text-neutralGray2 text-sm">COST</p>
                    <h3 className="font-bold">{convertAmount(plan.totalCost).amountString}</h3>
                  </div>
                  <div className="flex flex-col items-start justify-end gap-2">
                    <p className="font-medium text-neutralGray2 text-sm">PURCHASE DATE</p>
                    <h3 className="font-bold">
                      {formatInTimeZone(plan.createdAt, userData!.preference!.timezone, 'd LLL y')}
                    </h3>
                  </div>
                </div>
              </div>
              <div className="border bg-white row-span-2 col-span-3 rounded-md p-6 flex flex-col justify-between">
                <div className="h-full flex flex-col gap-4">
                  <h2 className="font-medium text-lg">How many sessions do you want?</h2>
                  <input
                    type="range"
                    value={range}
                    min={1}
                    max={10}
                    onChange={(e) => setRange(e.target.valueAsNumber < 1 ? 1 : e.target.valueAsNumber)}
                    className="form-range w-3/5 h-2 p-0 bg-neutralBlue1 accent-primary rounded-2xl focus:outline-none focus:ring-0 focus:shadow-none"
                    id="customRange1"
                  />
                  <p>{noOfSessions + ' Sessions'}</p>
                  <div className="flex justify-between py-4 pr-3">
                    <div>
                      <h3>Discount</h3>
                      <p className="font-semibold text-2xl">{`${discount}%`}</p>
                    </div>

                    <div>
                      <h3>Cost per Session</h3>
                      <p className="font-semibold text-2xl">{convertAmount(costPerSession).amountString}</p>
                    </div>
                  </div>
                </div>
                <Link to={'/contact'}>
                  <p className="text-xs text-primary underline">Contact Support for any queries</p>
                </Link>
              </div>

              <div className="bg-neutralBlue2 row-span-3 col-span-2 rounded-md shadow-lg">
                <div className="h-full flex flex-col justify-between px-6 py-10">
                  <div className="flex flex-col gap-3">
                    <p className="text-xs">{`${today.toLocaleDateString()}`}</p>
                    <h3 className="font-medium text-lg">{`Total Sessions :  ${noOfSessions}`}</h3>
                    <div className="flex justify-between">
                      <h3 className="font-medium text-lg">Amount :</h3>
                      <h3 className="font-medium text-lg">
                        {convertAmount(costPerSession * noOfSessions).amountString}
                      </h3>
                    </div>
                    <div className="flex justify-between">
                      <h3 className="font-medium text-lg">Discount :</h3>
                      <h3 className="font-medium text-lg">
                        {convertAmount((costPerSession * noOfSessions * discount) / 100).amountString}
                      </h3>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <hr />
                    <div className="flex justify-between">
                      <h3 className="font-medium text-lg">Total Amount :</h3>
                      <h3 className="font-medium text-lg">
                        {convertAmount((costPerSession * noOfSessions * (100 - discount)) / 100).amountString}
                      </h3>
                    </div>
                    <div className="flex pt-4 gap-4 items-center justify-center">
                      <div className="w-full max-w-[140px]">
                        <Button
                          text={'Pay with Cash'}
                          bgColor="bg-white"
                          borderColor="border-primary"
                          textColor="text-primary"
                          size="sm"
                          onClick={() => {
                            payWithCash();
                          }}
                        />
                      </div>
                      <div className="w-full max-w-[140px]">
                        <Button
                          text={'Pay Online'}
                          bgColor="bg-secondary"
                          borderColor="border-secondary"
                          textColor="text-white"
                          size="sm"
                          onClick={() => {
                            payOnline();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col gap-5 items-center justify-center py-10">
              <img src={UNDER_REVIEW} alt="Request under review" className="rounded-full" />
              <div className="flex flex-col items-center w-full gap-2">
                <span className="text-2xl font-medium">Thank you for purchasing a plan with us!</span>
                <span className="w-3/5 font-normal text-center">
                  We will notify you when admin confirms the reciept of your cash payment. Until then sit back and
                  relax!
                </span>
              </div>
              <div className="max-w-[200px] pt-10">
                <Button
                  text="Contact Support"
                  onClick={() => {}}
                  bgColor="bg-white"
                  size="lg"
                  borderColor="border-primary"
                  textColor="text-primary"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

export default ExtendPlan;
